import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import DatePicker from '../../components/DatePicker';
import { useToast } from '../../components/Toast';
import {
  fetchAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  setCurrentPage,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
} from '../../store/slices/announcementSlice';
import './Announcements.css';

const ITEMS_PER_PAGE = 9;

const TYPE_LABELS = {
  info: '情報',
  success: '成功',
  warning: '警告',
  danger: '危険',
};

const TARGET_LABELS = {
  healthtron: 'Healthtron',
  coupon: 'Coupon',
};

const formatDateTimeForApi = (dateStr, timeStr) => {
  if (!dateStr) return '';
  const time = timeStr || '00:00:00';
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  return `${dateStr} ${normalizedTime}`;
};

const parseApiDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return { date: '', time: '00:00' };
  const normalized = String(dateTimeStr).replace('T', ' ').slice(0, 19);
  const [datePart, timePart] = normalized.split(' ');
  return {
    date: datePart || '',
    time: timePart ? timePart.slice(0, 5) : '00:00',
  };
};

const buildLocalDate = (dateTimeStr) => {
  const { date, time } = parseApiDateTime(dateTimeStr);
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  const timeParts = (time || '00:00').split(':');
  const hours = Number(timeParts[0] || 0);
  const minutes = Number(timeParts[1] || 0);
  const seconds = Number(String(timeParts[2] || '0').split('.')[0]);
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

const getDefaultFormData = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  return {
    title: '',
    content: '',
    type: 'info',
    target: 'coupon',
    startDate: dateStr,
    startTime: '00:00',
    endDate: dateStr,
    endTime: '23:59',
    is_active: true,
  };
};

const announcementToFormData = (item) => {
  const start = parseApiDateTime(item.start_at);
  const end = parseApiDateTime(item.end_at);
  return {
    title: item.title || '',
    content: item.content || '',
    type: item.type || 'info',
    target: TARGET_LABELS[item.target] ? item.target : 'coupon',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    is_active: item.is_active !== undefined ? item.is_active : true,
  };
};

const formatDisplayDateTime = (dateTimeStr) => {
  const { date, time } = parseApiDateTime(dateTimeStr);
  if (!date) return '-';
  return `${date.replace(/-/g, '/')} ${time}`;
};

const isAnnouncementExpired = (item) => {
  const end = buildLocalDate(item?.end_at);
  if (!end) return false;
  return Date.now() > end.getTime();
};

const getAnnouncementStatus = (item) => {
  if (isAnnouncementExpired(item)) {
    return { label: '期限切れ', variant: 'expired' };
  }
  if (!item.is_active) {
    return { label: '無効', variant: 'inactive' };
  }
  const start = buildLocalDate(item.start_at);
  if (start && Date.now() >= start.getTime()) {
    return { label: '表示中', variant: 'active' };
  }
  return { label: '予定', variant: 'scheduled' };
};

const buildPayload = (formData) => ({
  title: formData.title.trim(),
  content: formData.content.trim(),
  type: formData.type,
  target: formData.target,
  start_at: formatDateTimeForApi(formData.startDate, formData.startTime),
  end_at: formatDateTimeForApi(formData.endDate, formData.endTime),
  is_active: formData.is_active,
});

const Announcements = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    announcements,
    currentPage,
    isLoading,
    isSubmitting,
    isCreateModalOpen,
    isEditModalOpen,
    editingAnnouncement,
  } = useSelector((state) => state.announcement);

  const [formData, setFormData] = useState(getDefaultFormData);

  const totalPages = Math.ceil(announcements.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentData = announcements.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    dispatch(fetchAnnouncements());
  }, [dispatch]);

  useEffect(() => {
    if (isCreateModalOpen) {
      setFormData(getDefaultFormData());
    }
  }, [isCreateModalOpen]);

  useEffect(() => {
    if (isEditModalOpen && editingAnnouncement) {
      setFormData(announcementToFormData(editingAnnouncement));
    }
  }, [isEditModalOpen, editingAnnouncement]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setCurrentPage(page));
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(createAnnouncement(buildPayload(formData)));

    if (createAnnouncement.fulfilled.match(result)) {
      toast.success('お知らせを作成しました');
    } else {
      toast.error(result?.payload || 'お知らせの作成に失敗しました');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (isAnnouncementExpired(editingAnnouncement)) {
      toast.warning('期限切れのお知らせは編集できません');
      dispatch(closeEditModal());
      return;
    }

    const result = await dispatch(
      updateAnnouncement({ id: editingAnnouncement.id, data: buildPayload(formData) })
    );

    if (updateAnnouncement.fulfilled.match(result)) {
      toast.success('お知らせを更新しました');
    } else {
      toast.error(result?.payload || 'お知らせの更新に失敗しました');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;

    const result = await dispatch(deleteAnnouncement(id));
    if (deleteAnnouncement.fulfilled.match(result)) {
      toast.success('お知らせを削除しました');
    } else {
      toast.error(result?.payload || 'お知らせの削除に失敗しました');
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage, '...', totalPages);
    }

    return pages;
  };

  const renderFormFields = (radioNameSuffix) => (
    <>
      <div className="announcement__form-group">
        <label className="announcement__form-label">タイトル</label>
        <input
          type="text"
          className="announcement__form-input"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="例: システムメンテナンスのお知らせ"
          maxLength={255}
          required
        />
      </div>

      <div className="announcement__form-group">
        <label className="announcement__form-label">内容</label>
        <textarea
          className="announcement__form-textarea"
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="お知らせの詳細を入力してください"
          rows={4}
          required
        />
      </div>

      <div className="announcement__form-group">
        <label className="announcement__form-label">種類</label>
        <div className="announcement__form-radio-group">
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <label key={value} className="announcement__form-radio">
              <input
                type="radio"
                name={`announcement-type-${radioNameSuffix}`}
                value={value}
                checked={formData.type === value}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="announcement__form-radio-input"
              />
              <span className="announcement__form-radio-custom"></span>
              <span className="announcement__form-radio-label">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="announcement__form-group">
        <label className="announcement__form-label">対象</label>
        <div className="announcement__form-radio-group">
          {Object.entries(TARGET_LABELS).map(([value, label]) => (
            <label key={value} className="announcement__form-radio">
              <input
                type="radio"
                name={`announcement-target-${radioNameSuffix}`}
                value={value}
                checked={formData.target === value}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                className="announcement__form-radio-input"
              />
              <span className="announcement__form-radio-custom"></span>
              <span className="announcement__form-radio-label">{label}</span>
            </label>
          ))}
        </div>
        <span className="announcement__form-hint">※ お知らせを表示するシステムを選択してください</span>
      </div>

      <div className="announcement__form-group">
        <label className="announcement__form-label">表示開始日時</label>
        <div className="announcement__datetime-row">
          <DatePicker
            value={formData.startDate}
            onChange={(date) => setFormData({ ...formData, startDate: date })}
          />
          <input
            type="time"
            className="announcement__form-time"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="announcement__form-group">
        <label className="announcement__form-label">表示終了日時</label>
        <div className="announcement__datetime-row">
          <DatePicker
            value={formData.endDate}
            onChange={(date) => setFormData({ ...formData, endDate: date })}
          />
          <input
            type="time"
            className="announcement__form-time"
            value={formData.endTime}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
            required
          />
        </div>
        <span className="announcement__form-hint">※ 終了日時は開始日時より後である必要があります</span>
      </div>

      <div className="announcement__form-group announcement__form-group--checkbox">
        <label className="announcement__form-checkbox-label">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="announcement__form-checkbox"
          />
          <span className="announcement__form-checkbox-custom">
            <svg viewBox="0 0 24 24" className="announcement__form-checkbox-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
            </svg>
          </span>
          <span>有効にする</span>
        </label>
        <span className="announcement__form-hint">※ 無効にするとログイン画面・メイン画面に表示されません</span>
      </div>
    </>
  );

  const renderModal = ({ isEdit, title, onClose, onSubmit }) => (
    <div className="announcement__modal-overlay">
      <div className="announcement__modal">
        <div className="announcement__modal-header">
          <h2 className="announcement__modal-title">{title}</h2>
          <button className="announcement__modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit} className="announcement__modal-form">
          <div className="announcement__modal-body">
            {renderFormFields(isEdit ? 'edit' : 'create')}
          </div>
          <div className="announcement__modal-footer">
            <button
              type="button"
              className="announcement__modal-btn announcement__modal-btn--cancel"
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="announcement__modal-btn announcement__modal-btn--submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (isEdit ? '保存中...' : '作成中...') : isEdit ? '保存' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="announcement">
        <div className="announcement__card">
          <h1 className="announcement__title">お知らせ管理</h1>

          <div className="announcement__scroll-area">
            <button
              className="announcement__create-btn"
              onClick={() => dispatch(openCreateModal())}
            >
              新規作成
              <span className="announcement__create-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="announcement__create-btn-icon-svg">
                  <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                </svg>
              </span>
            </button>

            {isLoading && <Loading text="読み込み中..." />}

            {!isLoading && (
              <div className="announcement__table-wrapper">
                <table className="announcement__table">
                  <thead className="announcement__table-head">
                    <tr className="announcement__table-row">
                      <th className="announcement__table-th announcement__table-th--number announcement__table-th-rounded-left">番号</th>
                      <th className="announcement__table-th">タイトル</th>
                      <th className="announcement__table-th">種類</th>
                      <th className="announcement__table-th">対象</th>
                      <th className="announcement__table-th">表示期間</th>
                      <th className="announcement__table-th">ステータス</th>
                      <th className="announcement__table-th announcement__table-th-rounded-right">操作</th>
                    </tr>
                  </thead>
                  <tbody className="announcement__table-body">
                    {currentData.map((item, index) => {
                      const status = getAnnouncementStatus(item);
                      const expired = isAnnouncementExpired(item);
                      return (
                        <tr
                          key={item.id}
                          className={`announcement__table-row${expired ? ' announcement__table-row--expired' : ''}`}
                        >
                          <td className="announcement__table-td announcement__table-td--number">
                            {startIndex + index + 1}
                          </td>
                          <td className="announcement__table-td announcement__table-td--title">{item.title}</td>
                          <td className="announcement__table-td">
                            <span className={`announcement__type-badge announcement__type-badge--${item.type || 'info'}`}>
                              {TYPE_LABELS[item.type] || TYPE_LABELS.info}
                            </span>
                          </td>
                          <td className="announcement__table-td">
                            <span className="announcement__target-badge">
                              {TARGET_LABELS[item.target] || item.target || '-'}
                            </span>
                          </td>
                          <td className="announcement__table-td announcement__table-td--period">
                            {formatDisplayDateTime(item.start_at)}
                            <span className="announcement__period-separator">~</span>
                            {formatDisplayDateTime(item.end_at)}
                          </td>
                          <td className="announcement__table-td">
                            <span className={`announcement__status announcement__status--${status.variant}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="announcement__table-td">
                            <div className="announcement__actions">
                              {!isAnnouncementExpired(item) && (
                                <button
                                  className="announcement__action-btn announcement__action-btn--edit"
                                  onClick={() => dispatch(openEditModal(item))}
                                >
                                  編集
                                </button>
                              )}
                              <button
                                className="announcement__action-btn announcement__action-btn--delete"
                                onClick={() => handleDelete(item.id)}
                              >
                                削除
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && announcements.length === 0 && (
              <div className="announcement__empty">
                <p>データがありません</p>
              </div>
            )}

            {!isLoading && announcements.length > 0 && totalPages > 1 && (
              <div className="announcement__pagination">
                <button
                  className="announcement__pagination-btn announcement__pagination-btn--nav"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    className={`announcement__pagination-btn ${
                      page === currentPage ? 'announcement__pagination-btn--active' : ''
                    } ${page === '...' ? 'announcement__pagination-btn--dots' : ''}`}
                    onClick={() => page !== '...' && handlePageChange(page)}
                    disabled={page === '...'}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="announcement__pagination-btn announcement__pagination-btn--nav"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>

        {isCreateModalOpen &&
          renderModal({
            isEdit: false,
            title: '新規お知らせ作成',
            onClose: () => dispatch(closeCreateModal()),
            onSubmit: handleCreateSubmit,
          })}

        {isEditModalOpen &&
          editingAnnouncement &&
          renderModal({
            isEdit: true,
            title: 'お知らせ編集',
            onClose: () => dispatch(closeEditModal()),
            onSubmit: handleEditSubmit,
          })}
      </div>
    </Layout>
  );
};

export default Announcements;
